import { Component } from '@theme/component';

/**
 * Minimum pointer movement (in pixels) before a pointerdown is treated as a drag
 * rather than a click, to avoid interfering with radio-label clicks.
 * @constant {number}
 */
const DRAG_THRESHOLD = 5;

/**
 * Wraps its children in a horizontally scrollable, scrollbar-less track that can
 * be dragged with the mouse/pointer. Based on the Announcement bar component's
 * structure; the drag mechanics are adapted from the Slideshow component's
 * pointer-drag handling, simplified for free scrolling (no snap/velocity settle).
 *
 * @extends {Component}
 */
export class DragScrollComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerdown', this.#handlePointerDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('pointerdown', this.#handlePointerDown);
  }

  /**
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    if (event.button !== 0) return;
    if (this.scrollWidth <= this.clientWidth) return;

    const startX = event.clientX;
    const startScrollLeft = this.scrollLeft;
    let moved = false;

    const controller = new AbortController();
    const { signal } = controller;

    const onPointerMove = (/** @type {PointerEvent} */ moveEvent) => {
      const delta = moveEvent.clientX - startX;

      if (!moved) {
        if (Math.abs(delta) < DRAG_THRESHOLD) return;

        moved = true;
        this.setPointerCapture(event.pointerId);
        this.toggleAttribute('dragging', true);
        document.addEventListener('click', preventClick, { once: true, capture: true, signal });
      }

      moveEvent.preventDefault();
      this.scrollLeft = startScrollLeft - delta;
    };

    const onPointerUp = () => {
      controller.abort();
      this.toggleAttribute('dragging', false);
    };

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
  };
}

/**
 * @param {Event} event
 */
function preventClick(event) {
  event.preventDefault();
  event.stopPropagation();
}

if (!customElements.get('drag-scroll-component')) {
  customElements.define('drag-scroll-component', DragScrollComponent);
}
